/**
 * 风险监控仪表盘组件
 *
 * 展示风险概览统计、趋势图表、高风险用户列表。
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, AreaChart, Area, Cell,
} from 'recharts';
import {
  safeguardAdminApi,
  type SafeguardOverviewStats,
  type SafeguardTrendItem,
  type HighRiskUser,
} from '../../services/safeguardAdminApi';

export const RiskDashboard: React.FC<{ autoRefresh?: boolean }> = ({ autoRefresh = true }) => {
  const [overview, setOverview] = useState<SafeguardOverviewStats | null>(null);
  const [trends, setTrends] = useState<SafeguardTrendItem[]>([]);
  const [highRiskUsers, setHighRiskUsers] = useState<HighRiskUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [ov, tr, hr] = await Promise.all([
        safeguardAdminApi.getOverviewStats(),
        safeguardAdminApi.getTrends(7),
        safeguardAdminApi.getHighRiskUsers(10),
      ]);
      setOverview(ov);
      setTrends(tr);
      setHighRiskUsers(hr);
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : '获取数据失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    if (!autoRefresh) return;
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [fetchData, autoRefresh]);

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="bg-white rounded-lg border border-gray-200 p-4 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-20 mb-2" />
              <div className="h-8 bg-gray-200 rounded w-16" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        {error}
      </div>
    );
  }

  if (!overview) return null;

  return (
    <div className="space-y-6">
      {/* 概览统计卡片 */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="24h审核总数"
          value={overview.total_checks_24h}
          color="blue"
        />
        <StatCard
          label="24h拦截次数"
          value={overview.blocked_count_24h}
          color="red"
        />
        <StatCard
          label="24h警告次数"
          value={overview.warned_count_24h}
          color="yellow"
        />
        <StatCard
          label="封禁用户数"
          value={overview.banned_users_count}
          color="purple"
        />
      </div>

      {/* 趋势图 */}
      {trends.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-3">7天违规趋势</h3>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={trends}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Area type="monotone" dataKey="blocked" stackId="1" stroke="#ef4444" fill="#fecaca" name="拦截" />
              <Area type="monotone" dataKey="warned" stackId="1" stroke="#f59e0b" fill="#fef3c7" name="警告" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* 触发词热度和来源分布 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* 触发词热度 */}
        {overview.top_matched_words.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h3 className="text-sm font-medium text-gray-700 mb-3">触发词热度 TOP10</h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={overview.top_matched_words.slice(0, 10)} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis type="number" tick={{ fontSize: 12 }} />
                <YAxis dataKey="word" type="category" width={80} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="count" name="次数" radius={[0, 4, 4, 0]}>
                  {overview.top_matched_words.slice(0, 10).map((_, index) => (
                    <Cell key={index} fill="#3b82f6" fillOpacity={1 - index * 0.08} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* 来源分布 */}
        {overview.source_distribution.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h3 className="text-sm font-medium text-gray-700 mb-3">来源分布</h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={overview.source_distribution}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="source" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="count" name="次数" radius={[4, 4, 0, 0]}>
                  {overview.source_distribution.map((_, index) => (
                    <Cell key={index} fill={['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6'][index % 4]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* 高风险用户列表 */}
      {highRiskUsers.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-3">高风险用户</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-2 px-3 text-gray-500 font-medium">用户</th>
                  <th className="text-left py-2 px-3 text-gray-500 font-medium">违规次数</th>
                  <th className="text-left py-2 px-3 text-gray-500 font-medium">最后违规</th>
                  <th className="text-left py-2 px-3 text-gray-500 font-medium">状态</th>
                </tr>
              </thead>
              <tbody>
                {highRiskUsers.map(u => (
                  <tr key={u.id} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="py-2 px-3">
                      <div>{u.nickname || u.email}</div>
                      <div className="text-xs text-gray-400">{u.email}</div>
                    </td>
                    <td className="py-2 px-3">
                      <span className="font-medium text-red-600">{u.violation_count}</span>
                    </td>
                    <td className="py-2 px-3 text-gray-500 text-xs">
                      {u.last_violation_at ? new Date(u.last_violation_at).toLocaleString('zh-CN') : '-'}
                    </td>
                    <td className="py-2 px-3">
                      <StatusBadge status={u.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const StatCard: React.FC<{ label: string; value: number; color: string }> = ({ label, value, color }) => {
  const colorMap: Record<string, string> = {
    blue: 'bg-blue-50 text-blue-700',
    red: 'bg-red-50 text-red-700',
    yellow: 'bg-yellow-50 text-yellow-700',
    purple: 'bg-purple-50 text-purple-700',
  };
  const valueColorMap: Record<string, string> = {
    blue: 'text-blue-600',
    red: 'text-red-600',
    yellow: 'text-yellow-600',
    purple: 'text-purple-600',
  };

  return (
    <div className={`rounded-lg border border-gray-200 p-4 ${colorMap[color] || ''}`}>
      <div className="text-sm text-gray-500">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${valueColorMap[color] || ''}`}>{value}</div>
    </div>
  );
};

export const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const styles: Record<string, string> = {
    active: 'bg-green-100 text-green-700',
    banned: 'bg-red-100 text-red-700',
  };
  const labels: Record<string, string> = {
    active: '正常',
    banned: '已封禁',
  };

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-700'}`}>
      {labels[status] || status}
    </span>
  );
};
